
import React, { useState, useEffect, useRef } from 'react';
import { supabase, joinOrgWithRole } from '../services/supabase';
import { sendGAEvent } from '../lib/gtag';
import { UserProfile, Organization, ASSIGNABLE_ROLES, COACH_ROLES } from '../types';
import { Building2, Users, Loader2, Plus, ArrowRight, ShieldOff, Database, Copy, Check, QrCode, Camera, X, AlertTriangle } from 'lucide-react';
import jsQR from 'jsqr';

interface OrgOnboardingProps {
  user: UserProfile;
  onOrgSelected: (orgId?: string) => void;
  availableOrgs: Organization[];
  onBypass: () => void;
  prefilledJoinCode?: string;
}

const SQL_SETUP_SCRIPT = `
-- 1. Create Organizations
create table if not exists public.organizations (
  id uuid default gen_random_uuid() primary key,
  name text not null,
  join_code text not null unique,
  created_at timestamp with time zone default timezone('utc'::text, now()) not null
);

-- 2. Create Profiles (if not exists)
create table if not exists public.profiles (
  id uuid references auth.users(id) on delete cascade primary key,
  email text,
  full_name text,
  updated_at timestamp with time zone
);

-- 3. Create Members Junction
create table if not exists public.organization_members (
  id uuid default gen_random_uuid() primary key,
  organization_id uuid references public.organizations(id) on delete cascade not null,
  user_id uuid references auth.users(id) on delete cascade,
  role text not null default 'other',
  is_admin boolean default false,
  status text default 'active',
  invited_name text,
  invited_email text,
  requested_role text,
  created_at timestamp with time zone default timezone('utc'::text, now()) not null,
  unique(organization_id, user_id)
);

-- 4. Create Teams
create table if not exists public.teams (
  id uuid default gen_random_uuid() primary key,
  organization_id uuid references public.organizations(id) on delete cascade not null,
  name text not null,
  age_group text,
  is_default boolean default false,
  is_favorite boolean default false,
  type text check (type in ('club', 'opponent')) default 'club',
  eligibility_requirements jsonb,
  coach_notes jsonb,
  created_at timestamp with time zone default timezone('utc'::text, now()) not null
);

-- 5. Create Players
create table if not exists public.players (
  id uuid default gen_random_uuid() primary key,
  organization_id uuid references public.organizations(id) on delete cascade not null,
  team_id uuid references public.teams(id) on delete set null,
  name text not null,
  jersey_number text,
  position text,
  rank integer,
  email text,
  phone text,
  parent1_name text,
  parent1_contact text,
  parent2_name text,
  parent2_contact text,
  goals text,
  notes text,
  photo_url text,
  grad_year text,
  height text,
  weight text,
  age integer,
  handedness text,
  eligibility jsonb default '{}'::jsonb,
  created_at timestamp with time zone default timezone('utc'::text, now()) not null
);

-- 6. Create Events
create table if not exists public.events (
  id uuid default gen_random_uuid() primary key,
  organization_id uuid references public.organizations(id) on delete cascade not null,
  type text not null,
  title text not null,
  date text not null,
  time text,
  location text,
  opponent text,
  stats jsonb default '[]'::jsonb,
  created_at timestamp with time zone default timezone('utc'::text, now()) not null
);

-- 7. Create Lineups
create table if not exists public.lineups (
  id uuid default gen_random_uuid() primary key,
  organization_id uuid references public.organizations(id) on delete cascade not null,
  team_id uuid references public.teams(id) on delete cascade not null,
  name text not null,
  positions jsonb not null,
  libero_assignments jsonb default '[]'::jsonb,
  is_default boolean default false,
  is_favorite boolean default false,
  created_at timestamp with time zone default timezone('utc'::text, now()) not null
);

-- 8. Enable RLS and simple policies
alter table public.organizations enable row level security;
alter table public.organization_members enable row level security;
alter table public.teams enable row level security;
alter table public.players enable row level security;
alter table public.events enable row level security;
alter table public.lineups enable row level security;
alter table public.profiles enable row level security;

create policy "Allow all for authenticated" on public.organizations for all using (auth.role() = 'authenticated');
create policy "Allow all for authenticated" on public.organization_members for all using (auth.role() = 'authenticated');
create policy "Allow all for authenticated" on public.teams for all using (auth.role() = 'authenticated');
create policy "Allow all for authenticated" on public.players for all using (auth.role() = 'authenticated');
create policy "Allow all for authenticated" on public.events for all using (auth.role() = 'authenticated');
create policy "Allow all for authenticated" on public.lineups for all using (auth.role() = 'authenticated');
create policy "Allow all for authenticated" on public.profiles for all using (auth.role() = 'authenticated');

-- 9. Setup User Trigger
create or replace function public.handle_new_user()
returns trigger as $$
begin
  insert into public.profiles (id, email, full_name)
  values (new.id, new.email, new.raw_user_meta_data->>'full_name');
  return new;
end;
$$ language plpgsql security definer;

drop trigger if exists on_auth_user_created on auth.users;
create trigger on_auth_user_created
  after insert on auth.users
  for each row execute procedure public.handle_new_user();

-- 10. MIGRATIONS (Run these to update existing tables)
do $$
begin
  if not exists (select 1 from information_schema.columns where table_name='teams' and column_name='type') then
    alter table public.teams add column type text check (type in ('club', 'opponent')) default 'club';
  end if;
  if not exists (select 1 from information_schema.columns where table_name='teams' and column_name='coach_notes') then
    alter table public.teams add column coach_notes jsonb;
  end if;
  if not exists (select 1 from information_schema.columns where table_name='teams' and column_name='eligibility_requirements') then
    alter table public.teams add column eligibility_requirements jsonb;
  end if;
  if not exists (select 1 from information_schema.columns where table_name='teams' and column_name='is_favorite') then
    alter table public.teams add column is_favorite boolean default false;
  end if;
  if not exists (select 1 from information_schema.columns where table_name='events' and column_name='stats') then
    alter table public.events add column stats jsonb default '[]'::jsonb;
  end if;
  if not exists (select 1 from information_schema.columns where table_name='lineups' and column_name='libero_assignments') then
    alter table public.lineups add column libero_assignments jsonb default '[]'::jsonb;
  end if;
  if not exists (select 1 from information_schema.columns where table_name='lineups' and column_name='is_favorite') then
    alter table public.lineups add column is_favorite boolean default false;
  end if;
  if not exists (select 1 from information_schema.columns where table_name='players' and column_name='grad_year') then
    alter table public.players add column grad_year text;
  end if;
  if not exists (select 1 from information_schema.columns where table_name='players' and column_name='height') then
    alter table public.players add column height text;
  end if;
  if not exists (select 1 from information_schema.columns where table_name='players' and column_name='weight') then
    alter table public.players add column weight text;
  end if;
  if not exists (select 1 from information_schema.columns where table_name='players' and column_name='age') then
    alter table public.players add column age integer;
  end if;
  if not exists (select 1 from information_schema.columns where table_name='players' and column_name='handedness') then
    alter table public.players add column handedness text;
  end if;
  if not exists (select 1 from information_schema.columns where table_name='players' and column_name='eligibility') then
    alter table public.players add column eligibility jsonb default '{}'::jsonb;
  end if;
  -- New org_members columns
  if not exists (select 1 from information_schema.columns where table_name='organization_members' and column_name='is_admin') then
    alter table public.organization_members add column is_admin boolean default false;
  end if;
  if not exists (select 1 from information_schema.columns where table_name='organization_members' and column_name='status') then
    alter table public.organization_members add column status text default 'active';
  end if;
  if not exists (select 1 from information_schema.columns where table_name='organization_members' and column_name='invited_name') then
    alter table public.organization_members add column invited_name text;
  end if;
  if not exists (select 1 from information_schema.columns where table_name='organization_members' and column_name='invited_email') then
    alter table public.organization_members add column invited_email text;
  end if;
  if not exists (select 1 from information_schema.columns where table_name='organization_members' and column_name='requested_role') then
    alter table public.organization_members add column requested_role text;
  end if;
end $$;

-- Make existing head_coaches admins
update public.organization_members set is_admin = true where role = 'head_coach' and (is_admin is null or is_admin = false);

-- Expand role constraint to new roles
alter table public.organization_members drop constraint if exists organization_members_role_check;
alter table public.organization_members add constraint organization_members_role_check
  check (role in ('head_coach', 'assistant_coach', 'coach', 'team_manager', 'stat_keeper', 'parent', 'other', 'assistant', 'player'));
`;

const OrgOnboarding: React.FC<OrgOnboardingProps> = ({ user, onOrgSelected, availableOrgs, onBypass, prefilledJoinCode }) => {
  const [view, setView] = useState<'select' | 'create' | 'join'>('select');
  const [newOrgName, setNewOrgName] = useState('');
  const [joinCode, setJoinCode] = useState(prefilledJoinCode || '');
  const [selectedRole, setSelectedRole] = useState('assistant_coach');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSql, setShowSql] = useState(false);
  const [copied, setCopied] = useState(false);
  const [joinedPending, setJoinedPending] = useState(false);
  const [betaCode, setBetaCode] = useState('');
  const [showBetaField, setShowBetaField] = useState(false);
  const [pendingOrgId, setPendingOrgId] = useState<string | null>(null);

  // QR Scanner state
  const [showScanner, setShowScanner] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animFrameRef = useRef<number>(0);
  const streamRef = useRef<MediaStream | null>(null);

  // Auto-open join view if prefilled code
  useEffect(() => {
    if (prefilledJoinCode) setView('join');
  }, [prefilledJoinCode]);

  const generateJoinCode = () => Math.random().toString(36).substring(2, 8).toUpperCase();

  const isTableMissingError = (errMessage: string) =>
    errMessage.includes('Could not find the table') ||
    (errMessage.includes('relation') && errMessage.includes('does not exist')) ||
    (errMessage.includes('column') && errMessage.includes('does not exist'));

  const copySql = () => {
    navigator.clipboard.writeText(SQL_SETUP_SCRIPT);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // QR Scanner logic
  const startScanner = async () => {
    setScanError(null);
    setShowScanner(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        scanFrame();
      }
    } catch (e) {
      setScanError('Camera access denied. Please allow camera access or enter the code manually.');
    }
  };

  const scanFrame = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.readyState < 2) {
      animFrameRef.current = requestAnimationFrame(scanFrame);
      return;
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const code = jsQR(imageData.data, imageData.width, imageData.height, { inversionAttempts: 'dontInvert' });
    if (code?.data) {
      stopScanner();
      try {
        const url = new URL(code.data);
        const extracted = url.searchParams.get('join');
        if (extracted) {
          setJoinCode(extracted.toUpperCase());
        } else {
          // Maybe it's just the raw code
          const raw = code.data.trim().toUpperCase();
          if (/^[A-Z0-9]{6}$/.test(raw)) setJoinCode(raw);
          else setScanError('QR code did not contain a valid join code.');
        }
      } catch {
        const raw = code.data.trim().toUpperCase();
        if (/^[A-Z0-9]{6}$/.test(raw)) setJoinCode(raw);
        else setScanError('QR code did not contain a valid join code.');
      }
    } else {
      animFrameRef.current = requestAnimationFrame(scanFrame);
    }
  };

  const stopScanner = () => {
    cancelAnimationFrame(animFrameRef.current);
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    setShowScanner(false);
  };

  const handleCreateOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setShowSql(false);
    const code = generateJoinCode();
    try {
      // Compute beta expiry: last day of the month 6 months from now
      const now = new Date();
      const betaExpiresAt = new Date(now.getFullYear(), now.getMonth() + 7, 0).toISOString();

      const { data: orgData, error: orgError } = await supabase
        .from('organizations')
        .insert([{ name: newOrgName, join_code: code, is_beta_tester: true, beta_approval_date: now.toISOString(), beta_expires_at: betaExpiresAt }])
        .select()
        .single();
      if (orgError) throw orgError;

      const { error: memberError } = await supabase
        .from('organization_members')
        .insert([{ organization_id: orgData.id, user_id: user.id, role: 'head_coach', is_admin: true, status: 'active' }]);
      if (memberError) throw memberError;

      // Try beta code redemption if provided — must await before onOrgSelected
      // so the org loads with approved_at already set
      if (betaCode.trim()) {
        try {
          const r = await fetch('/api/beta/redeem-code', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ orgId: orgData.id, code: betaCode.trim() }),
          });
          const d = await r.json();
          if (d.approved === true) {
            onOrgSelected(orgData.id);
            return;
          }
        } catch (e) {
          console.warn('[beta] redeem-code failed:', e);
        }
        // Code was entered but didn't match — hold the org and surface the error
        setPendingOrgId(orgData.id);
        setLoading(false);
        return;
      }

      // No code entered — notify admin and proceed to pending queue
      fetch('/api/beta/notify-org-created', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orgId: orgData.id, orgName: newOrgName, creatorName: user.fullName, creatorEmail: user.email }),
      }).then(r => r.json()).then(d => { if (d.error) console.warn('[beta] notify-org-created error:', d.error); }).catch(e => console.warn('[beta] notify-org-created fetch failed:', e));
      sendGAEvent('event', 'waitlist_joined', { method: 'email' });
      onOrgSelected(orgData.id);
    } catch (err: any) {
      setError(err.message);
      if (isTableMissingError(err.message)) setShowSql(true);
    } finally {
      setLoading(false);
    }
  };

  const handleRetryCode = async () => {
    if (!pendingOrgId || !betaCode.trim()) return;
    setLoading(true);
    try {
      const r = await fetch('/api/beta/redeem-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orgId: pendingOrgId, code: betaCode.trim() }),
      });
      const d = await r.json();
      if (d.approved === true) {
        onOrgSelected(pendingOrgId);
      } else {
        setBetaCode('');
      }
    } catch (e) {
      console.warn('[beta] redeem-code retry failed:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleJoinWaitlist = () => {
    if (!pendingOrgId) return;
    fetch('/api/beta/notify-org-created', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orgId: pendingOrgId, orgName: newOrgName, creatorName: user.fullName, creatorEmail: user.email }),
    }).catch(e => console.warn('[beta] notify-org-created fetch failed:', e));
    onOrgSelected(pendingOrgId);
  };

  const handleJoinOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { data: orgData, error: orgError } = await supabase
        .from('organizations')
        .select('id, name')
        .eq('join_code', joinCode.toUpperCase())
        .single();
      if (orgError || !orgData) throw new Error('Invalid Organization Code');

      const { data: existing } = await supabase
        .from('organization_members')
        .select('id, status')
        .eq('organization_id', orgData.id)
        .eq('user_id', user.id)
        .single();

      if (existing) {
        if (existing.status === 'pending') {
          setJoinedPending(true);
          return;
        }
        onOrgSelected(orgData.id);
        return;
      }

      const result = await joinOrgWithRole(orgData.id, user.id, selectedRole);

      if (result.needsApproval) {
        setJoinedPending(true);
      } else {
        // Notify admin of new active member (best-effort)
        fetch('/api/beta/notify-member-joined', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ orgName: orgData.name, memberName: user.fullName, memberEmail: user.email, role: result.role }),
        }).catch(() => {});
        // Check membership milestones (best-effort)
        fetch('/api/beta/notify-member-milestone', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ orgId: orgData.id, orgName: orgData.name }),
        }).catch(() => {});
        onOrgSelected(orgData.id);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (joinedPending) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 w-full max-w-md text-center animate-fade-in-up">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-amber-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Request Pending</h2>
          <p className="text-slate-500 mb-6">
            Your request to join as a <strong>{ASSIGNABLE_ROLES.find(r => r.value === selectedRole)?.label || selectedRole}</strong> has been submitted.
            An admin needs to approve your access before you can proceed. You'll be able to log in once approved.
          </p>
          <button onClick={onBypass} className="text-slate-400 hover:text-slate-600 text-sm flex items-center justify-center gap-2 mx-auto">
            <ShieldOff className="w-4 h-4" /> Use Offline / Demo Mode while waiting
          </button>
        </div>
      </div>
    );
  }

  if (view === 'select') {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
        <div className="w-full max-w-2xl text-center mb-8 animate-fade-in-down">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Welcome, {user.fullName}!</h1>
          <p className="text-slate-500">Select an organization to continue or create a new one.</p>
        </div>

        <div className="w-full max-w-3xl space-y-6">
          {availableOrgs.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-fade-in-up">
              {availableOrgs.map(org => (
                <button key={org.id} onClick={() => onOrgSelected(org.id)}
                  className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 hover:border-blue-500 hover:shadow-md transition-all text-left flex items-center justify-between group">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center group-hover:bg-blue-50 transition-colors">
                      <Building2 className="w-5 h-5 text-slate-600 group-hover:text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900">{org.name}</h3>
                      <p className="text-xs text-slate-500 capitalize">{org.role?.replace(/_/g, ' ')}</p>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-blue-500" />
                </button>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-slate-200">
            <button onClick={() => setView('create')}
              className="bg-white p-6 rounded-xl shadow-sm border-2 border-dashed border-slate-300 hover:border-blue-500 hover:bg-blue-50 transition-all group text-left flex flex-col items-center text-center justify-center h-full min-h-[160px]">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                <Plus className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="font-bold text-slate-900 mb-1">Create New Organization</h3>
              <p className="text-slate-500 text-xs">Start a new club or team space</p>
            </button>

            <button onClick={() => setView('join')}
              className="bg-white p-6 rounded-xl shadow-sm border-2 border-dashed border-slate-300 hover:border-purple-500 hover:bg-purple-50 transition-all group text-left flex flex-col items-center text-center justify-center h-full min-h-[160px]">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="font-bold text-slate-900 mb-1">Join Organization</h3>
              <p className="text-slate-500 text-xs">Enter a code or scan a QR code</p>
            </button>
          </div>

          <div className="text-center pt-8">
            <button onClick={onBypass} className="text-slate-400 hover:text-slate-600 text-sm flex items-center justify-center gap-2 mx-auto">
              <ShieldOff className="w-4 h-4" /> Use Offline / Demo Mode
            </button>
          </div>
        </div>

        <div className="mt-8">
          <button onClick={() => setShowSql(!showSql)} className="text-xs text-slate-300 hover:text-slate-500 flex items-center gap-1">
            <Database className="w-3 h-3" /> {showSql ? 'Hide SQL Helper' : 'Show SQL Helper'}
          </button>
          {showSql && (
            <div className="mt-4 bg-slate-900 rounded-lg p-4 text-xs text-slate-300 relative overflow-hidden max-w-lg mx-auto">
              <p className="mb-2 text-blue-300 font-bold">Database Setup Script</p>
              <p className="mb-3">Run this SQL in your Supabase SQL Editor if tables/columns are missing:</p>
              <div className="bg-black/50 p-2 rounded max-h-40 overflow-y-auto font-mono mb-2">{SQL_SETUP_SCRIPT}</div>
              <button type="button" onClick={copySql}
                className="w-full py-2 bg-blue-600 text-white rounded font-bold hover:bg-blue-700 flex items-center justify-center gap-2">
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Copy SQL Script'}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (view === 'create' && pendingOrgId) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 w-full max-w-md animate-fade-in-up">
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Code Not Recognized</h2>
          <p className="text-slate-500 mb-6">That beta access code didn't match. Try a different code, or join the waitlist for manual review.</p>

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Beta Access Code</label>
              <input autoFocus type="text"
                className="w-full p-3 border border-slate-300 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 uppercase tracking-widest"
                placeholder="Enter code"
                value={betaCode} onChange={e => setBetaCode(e.target.value.toUpperCase())} />
            </div>
            <button type="button" onClick={handleRetryCode} disabled={loading || !betaCode.trim()}
              className="w-full py-3 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
              {loading && <Loader2 className="w-5 h-5 animate-spin" />} Try Code
            </button>
            <div className="relative flex items-center gap-3">
              <div className="flex-1 border-t border-slate-200" />
              <span className="text-xs text-slate-400">or</span>
              <div className="flex-1 border-t border-slate-200" />
            </div>
            <button type="button" onClick={handleJoinWaitlist}
              className="w-full py-3 text-slate-600 font-semibold hover:bg-slate-50 rounded-xl border border-slate-200 transition-colors">
              Continue Without Code →
            </button>
            <p className="text-xs text-slate-400 text-center">Joining the waitlist means we'll manually review your request, typically within 24 hours.</p>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'create') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 w-full max-w-md animate-fade-in-up">
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Create Organization</h2>
          <p className="text-slate-500 mb-6">Set up a workspace for your team. You'll be the admin.</p>

          <form onSubmit={handleCreateOrg} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Organization Name</label>
              <input autoFocus type="text" required
                className="w-full p-3 border border-slate-300 rounded-xl outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. West High Volleyball"
                value={newOrgName} onChange={e => setNewOrgName(e.target.value)} />
            </div>

            {!showBetaField ? (
              <button type="button" onClick={() => setShowBetaField(true)}
                className="text-xs text-slate-400 hover:text-slate-600 underline underline-offset-2">
                Have a beta access code?
              </button>
            ) : (
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Beta Access Code</label>
                <input type="text" autoFocus
                  className="w-full p-3 border border-slate-300 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 uppercase tracking-widest"
                  placeholder="Enter code"
                  value={betaCode} onChange={e => setBetaCode(e.target.value.toUpperCase())} />
              </div>
            )}

            {error && <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm border border-red-100">{error}</div>}

            {showSql && (
              <div className="bg-slate-900 rounded-lg p-4 text-xs text-slate-300 relative overflow-hidden">
                <p className="mb-2 text-red-300 font-bold">Database Issues Detected</p>
                <p className="mb-3">Run this SQL in Supabase:</p>
                <div className="bg-black/50 p-2 rounded max-h-40 overflow-y-auto font-mono mb-2">{SQL_SETUP_SCRIPT}</div>
                <button type="button" onClick={copySql}
                  className="w-full py-2 bg-blue-600 text-white rounded font-bold hover:bg-blue-700 flex items-center justify-center gap-2">
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied ? 'Copied!' : 'Copy SQL Script'}
                </button>
              </div>
            )}

            <button type="submit" disabled={loading}
              className="w-full py-3 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-colors flex items-center justify-center gap-2">
              {loading && <Loader2 className="w-5 h-5 animate-spin" />} Create
            </button>
            <button type="button" onClick={() => { setView('select'); setError(null); setShowSql(false); }}
              className="w-full py-3 text-slate-500 font-bold hover:bg-slate-50 rounded-xl">Cancel</button>
          </form>
        </div>
      </div>
    );
  }

  if (view === 'join') {
    const isCoachRole = COACH_ROLES.includes(selectedRole);

    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        {/* QR Scanner Modal */}
        {showScanner && (
          <div className="fixed inset-0 bg-black/80 z-50 flex flex-col items-center justify-center p-4">
            <div className="bg-white rounded-xl overflow-hidden w-full max-w-sm">
              <div className="p-4 flex items-center justify-between border-b border-slate-100">
                <h3 className="font-bold text-slate-900 flex items-center gap-2">
                  <QrCode className="w-4 h-4 text-blue-600" /> Scan QR Code
                </h3>
                <button onClick={stopScanner} className="p-1 hover:bg-slate-100 rounded-lg">
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
              <div className="relative bg-black aspect-square">
                <video ref={videoRef} className="w-full h-full object-cover" playsInline muted />
                <canvas ref={canvasRef} className="hidden" />
                {/* Scanning overlay */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-48 h-48 border-2 border-white rounded-lg opacity-70 relative">
                    <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-blue-400 rounded-tl" />
                    <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-blue-400 rounded-tr" />
                    <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-blue-400 rounded-bl" />
                    <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-blue-400 rounded-br" />
                  </div>
                </div>
              </div>
              {scanError && (
                <div className="p-3 bg-red-50 text-red-600 text-sm border-t border-red-100">{scanError}</div>
              )}
              <div className="p-3 text-center text-xs text-slate-400">Point camera at a SideoutIQ QR code</div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-lg p-8 w-full max-w-md animate-fade-in-up">
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Join Organization</h2>
          <p className="text-slate-500 mb-6">Enter the code from your coach or scan a QR code.</p>

          <form onSubmit={handleJoinOrg} className="space-y-5">
            {/* Join Code */}
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Join Code</label>
              <div className="flex gap-2">
                <input autoFocus type="text" required maxLength={6}
                  className="flex-1 p-3 border border-slate-300 rounded-xl outline-none focus:ring-2 focus:ring-purple-500 text-center font-mono text-2xl tracking-widest uppercase"
                  placeholder="ABC123"
                  value={joinCode} onChange={e => setJoinCode(e.target.value.toUpperCase())} />
                <button type="button" onClick={startScanner}
                  className="bg-slate-100 hover:bg-slate-200 border border-slate-300 px-3 rounded-xl text-slate-600 transition-colors"
                  title="Scan QR Code">
                  <QrCode className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Role Selection */}
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Your Role</label>
              <select value={selectedRole} onChange={e => setSelectedRole(e.target.value)}
                className="w-full p-3 border border-slate-300 rounded-xl outline-none focus:ring-2 focus:ring-purple-500 bg-white text-slate-700 font-medium">
                {ASSIGNABLE_ROLES.map(r => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
              {isCoachRole && (
                <div className="mt-2 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>Coach roles require admin approval. Your access will be pending until approved.</span>
                </div>
              )}
            </div>

            {error && <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm border border-red-100">{error}</div>}

            <button type="submit" disabled={loading}
              className="w-full py-3 bg-purple-600 text-white font-bold rounded-xl hover:bg-purple-700 transition-colors flex items-center justify-center gap-2">
              {loading && <Loader2 className="w-5 h-5 animate-spin" />}
              {isCoachRole ? 'Request to Join' : 'Join'}
            </button>
            <button type="button" onClick={() => { setView('select'); setError(null); }}
              className="w-full py-3 text-slate-500 font-bold hover:bg-slate-50 rounded-xl">Cancel</button>
          </form>
        </div>
      </div>
    );
  }

  return null;
};

export default OrgOnboarding;
